import * as React from 'react';

/**
 * Types `text` out left to right, once, with a caret that goes when it finishes — Google's
 * search-box intro. Deliberately not a loop: text that keeps moving pulls the eye every few
 * seconds on a page people work in all day. Screen readers get the whole text at once;
 * reduced motion shows it whole from the start.
 */
export function Typewriter({ text, speed = 40 }: { text: string; speed?: number }) {
  const [shown, setShown] = React.useState(0);
  React.useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(text.length);
      return;
    }
    setShown(0);
    const timer = window.setInterval(() => {
      setShown((n) => {
        if (n + 1 >= text.length) window.clearInterval(timer);
        return Math.min(n + 1, text.length);
      });
    }, speed);
    return () => window.clearInterval(timer);
  }, [text, speed]);

  const done = shown >= text.length;
  return (
    <>
      <span className="sr-only">{text}</span>
      <span aria-hidden data-testid="typewriter">
        {text.slice(0, shown)}
        {!done && <span className="typewriter-caret" />}
      </span>
    </>
  );
}
